import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialSymbol } from 'react-material-symbols';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import AuthLayout from '../components/AuthLayout.js';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form.tsx';
import { Input } from '../components/ui/input.tsx';
import useAuth from '../hooks/useAuth.js';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
});

const MainDashboardForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, forgotPasswordLoading, forgotPasswordSuccess } =
    useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values) => {
    if (forgotPasswordLoading) return;

    try {
      await forgotPassword({ email: values.email });
    } catch (error) {
      console.error('Forgot Password failed:', error);
      toast.error(
        error.message || 'Failed to Forgot Password. Please try again.'
      );
      form.setError('root', {
        type: 'manual',
        message:
          error.message ||
          'Failed Forgot Password Process. Please try again later.',
      });
    }
  };

  useEffect(() => {
    let timer;
    if (forgotPasswordSuccess) {
      timer = setTimeout(() => {
        navigate('/login');
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [forgotPasswordSuccess, navigate]);

  return (
    <AuthLayout>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col space-y-12 h-3/5 justify-center"
        >
          <div>
            <h1 className="font-medium text-2xl md:text-3xl">
              Forgot Password?
            </h1>
            <p className="text-[#828282] text-lg md:text-xl font-light">
              No worries, we will send you reset instructions.
            </p>
          </div>
          <div>
            {form.formState.errors.root && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
                {form.formState.errors.root.message}
              </div>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <span className="flex items-center border border-[#e5e5e5] rounded-sm h-10 px-2">
                      <MaterialSymbol
                        icon="mail"
                        size={24}
                        grade={0}
                        weight={300}
                        color="black"
                      />
                      <Input
                        placeholder="example@mail.com"
                        {...field}
                        disabled={forgotPasswordLoading}
                        className="outline-none border-none focus:outline-none focus:border-none focus-visible:ring-0 focus-visible:outline-none focus-visible:border-none rounded-none h-7"
                      />
                    </span>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {forgotPasswordSuccess && (
              <div className="p-3 text-sm text-green-600 bg-green-50 rounded-md">
                <p>You will be redirected to login in 5 seconds.</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={forgotPasswordSuccess}
              className="w-full h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {forgotPasswordLoading ? (
                <div className="flex items-center justify-center w-full">
                  <LoadingSpinner size="sm" className="mr-2" />
                  <span>Loading...</span>
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
};

export default MainDashboardForgotPassword;
